import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { DebtCollectionTaskFindManyQueryInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-find-many-query.dto';
import type { DebtCollectionTaskPageInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-page.dto';
import type { DebtCollectionTaskFormInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-form.dto';
import type { DebtCollectionTaskCreateInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-create.dto';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import type { DebtCollectionTaskFindInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-find.dto';
import type { DebtCollectionTaskSwitchStatusInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-switch-status.dto';
import type { QueryWithSelectInterface } from '@zaoblako/shared/common/interfaces/query-with-select.interface';
import type { CommentaryFilePageInterface } from '@zaoblako/shared/modules/storage/dtos/commentary-file/commentary-file-page.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';

@Injectable({
  providedIn: 'root'
})
export class DebtCollectionTaskService extends BaseService {
  protected baseUrl = '/debts/debt-collection/task';

  public findMany(query?: DebtCollectionTaskFindManyQueryInterface): Observable<DebtCollectionTaskPageInterface> {
    return this.http.get<DebtCollectionTaskPageInterface>(`${this.baseUrl}/items`, {
      params: query ? this.objectToHttpParams(query) : {}
    });
  }

  public findOne(id: number, query?: QueryWithSelectInterface): Observable<DebtCollectionTaskFindInterface> {
    return this.http.get<DebtCollectionTaskFindInterface>(`${this.baseUrl}/${id}`, {
      params: query ? this.objectToHttpParams(query) : {}
    });
  }

  public createOne(body: DebtCollectionTaskCreateInterface): Observable<DebtCollectionTaskFindInterface> {
    return this.http.post<DebtCollectionTaskFindInterface>(this.baseUrl, body);
  }

  public updateOne(id: number, body: DebtCollectionTaskFormInterface): Observable<DebtCollectionTaskFindInterface> {
    return this.http.put<DebtCollectionTaskFindInterface>(`${this.baseUrl}/${id}`, body);
  }

  public deleteOne(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  public switchStatus(id: number, body: DebtCollectionTaskSwitchStatusInterface): Observable<DebtCollectionTaskFindInterface> {
    return this.http.post<DebtCollectionTaskFindInterface>(`${this.baseUrl}/${id}/switch-status`, body);
  }

  public getHistory(id: number, queryParams: PaginationQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/${id}/history`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getFiles(taskId: number): Observable<CommentaryFilePageInterface> {
    return this.http.get<CommentaryFilePageInterface>(`${this.baseUrl}/${taskId}/files`);
  }

  public getFindManyDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(this.baseUrl);
  }

}
